import {
  Box,
  Button,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { ArrowForward, AutoAwesome, DeleteOutlined } from '@mui/icons-material'
import './SupplierQuotesSidebar.css'
import axios from 'axios'
import { useState } from 'react'
import ImportSupplierQuoteTemplateDialog from './ImportSupplierQuoteTemplateDialog'
import AiSupplierQuoteImportDialog from './AiSupplierQuoteImportDialog'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import SupplierQuoteNumberDialog from './SupplierQuoteNumberDialog'
import { vendorQuoteType } from '../../types'
import { formatCurrency } from '../../helper'
import { BundleLineItem } from './lineItemPricing'

const IMPRES_CATALOG_SOURCE = 'ImpresCatalog'

function countLineItemsForVendorQuote(lineItems: BundleLineItem[], vendorQuoteId: string): number {
  return lineItems.reduce((count, item) => {
    if (item.LineNumber === -1) {
      return count + (item.children?.filter(child => child.VendorQuoteId === vendorQuoteId).length ?? 0)
    }
    return item.VendorQuoteId === vendorQuoteId ? count + 1 : count
  }, 0)
}

const API_SUPPLIERS = ['TD Synnex', 'Ingram Micro', 'Carahsoft', 'Dell'] as const
type ApiSupplier = (typeof API_SUPPLIERS)[number]

type ApiSupplierConfig = { endpoint: string; fieldLabel: string; bodyKey: string }

const API_SUPPLIER_CONFIGS: Partial<Record<ApiSupplier, ApiSupplierConfig>> = {
  Carahsoft: {
    endpoint: 'carahsoftQuote',
    fieldLabel: 'Carahsoft Supplier Quote #',
    bodyKey: 'carahsoftQuoteNumber',
  },
  'TD Synnex': {
    endpoint: 'tdsynnexQuote',
    fieldLabel: 'TD Synnex Quote #',
    bodyKey: 'tdsynnexQuoteNumber',
  },
}

const TABLE_COLUMNS = [
  'Supplier Name',
  'Supplier Quote #',
  'Expiration Date',
  'Total Amount',
  'File Uploaded',
  'Supplier Quote',
] as const

export default function SupplierQuotesSidebar({
  onClose,
  quoteId,
  vendorQuotes,
  lineItems,
  onDeleteVendorQuote,
  viewOnly = false,
}: {
  onClose: () => void
  quoteId: string
  vendorQuotes: vendorQuoteType[]
  lineItems: BundleLineItem[]
  onDeleteVendorQuote?: (vendorQuoteId: string) => Promise<void>
  viewOnly?: boolean
}) {
  const [importTemplateOpen, setImportTemplateOpen] = useState(false)
  const [aiImportOpen, setAiImportOpen] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const [activeApiSupplier, setActiveApiSupplier] = useState<ApiSupplier | null>(null)
  const [apiDialogOpen, setApiDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<vendorQuoteType | null>(null)
  const activeConfig = activeApiSupplier ? API_SUPPLIER_CONFIGS[activeApiSupplier] : null
  const importedQuotes = vendorQuotes.filter(vendorQuote => vendorQuote.Source !== IMPRES_CATALOG_SOURCE)
  const catalogQuotes = vendorQuotes.filter(vendorQuote => vendorQuote.Source === IMPRES_CATALOG_SOURCE)
  const columns = viewOnly ? TABLE_COLUMNS : [...TABLE_COLUMNS, '']

  const handleDeleteConfirm = () => {
    if (!deleteTarget || !onDeleteVendorQuote) return
    onDeleteVendorQuote(deleteTarget.VendorQuoteId)
      .catch(err => console.error(err))
      .finally(() => setDeleteTarget(null))
  }

  const handleDownloadTemplate = () => {
    setDownloadingTemplate(true)
    axios
      .get('/vendorTemplate', { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = 'Supplier Quote Template.xlsx'
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch(err => console.error(err))
      .finally(() => setDownloadingTemplate(false))
  }

  return (
    <div className="supplier-quotes-sidebar">
      <Box display="flex" justifyContent="space-between" alignItems="center" padding={2}>
        <Typography variant="h6">Supplier Quotes</Typography>
        <IconButton onClick={onClose} aria-label="Close supplier quotes sidebar">
          <ArrowForward />
        </IconButton>
      </Box>
      <Divider />
      <Box padding={2} display="flex" flexDirection="column" gap={2} overflow="auto">
        {!viewOnly && (
          <Box display="flex" flexDirection="column" gap={1.75}>
            <Typography>Templates</Typography>
            <Button variant="outlined" fullWidth disabled={downloadingTemplate} onClick={handleDownloadTemplate}>
              Download Supplier Quote Template
            </Button>
            <Button variant="outlined" fullWidth onClick={() => setImportTemplateOpen(true)}>
              Import Supplier Quote Template
            </Button>
            <Typography>Import Quote with AI</Typography>
            <Button
              className="ai-import-button"
              variant="contained"
              fullWidth
              startIcon={<AutoAwesome />}
              onClick={() => setAiImportOpen(true)}
            >
              Import Supplier Quote (AI)
            </Button>
            <Typography>Import from Supplier (via API)</Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.75}>
              {API_SUPPLIERS.map(supplier => {
                const config = API_SUPPLIER_CONFIGS[supplier]
                return (
                  <Tooltip key={supplier} title={config ? '' : 'Coming soon'}>
                    <span>
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled={!config}
                        onClick={() => {
                          setActiveApiSupplier(supplier)
                          setApiDialogOpen(true)
                        }}
                      >
                        {supplier}
                      </Button>
                    </span>
                  </Tooltip>
                )
              })}
            </Box>
          </Box>
        )}
        <Typography>Imported Supplier Quotes</Typography>
        <Table className="supplier-quotes-table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column || 'actions'}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {importedQuotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No supplier quotes found
                </TableCell>
              </TableRow>
            ) : (
              importedQuotes.map(vendorQuote => (
                <TableRow key={vendorQuote.VendorQuoteId}>
                  <TableCell>{vendorQuote.VendorName ?? '-'}</TableCell>
                  <TableCell>{vendorQuote.VendorQuoteNumber ?? '-'}</TableCell>
                  <TableCell>
                    {vendorQuote.ExpirationDate ? new Date(vendorQuote.ExpirationDate).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>{formatCurrency(vendorQuote.TotalAmount)}</TableCell>
                  {/* No file-reference column exists on VendorQuote yet - see docs/supplier-quotes-sidebar.md */}
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  {!viewOnly && (
                    <TableCell>
                      <IconButton size="small" onClick={() => setDeleteTarget(vendorQuote)}>
                        <DeleteOutlined />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Typography>Added from IMPRES Catalog</Typography>
        <Table className="supplier-quotes-table">
          <TableHead>
            <TableRow>
              <TableCell>Supplier Name</TableCell>
              <TableCell>Items</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {catalogQuotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No items added from the IMPRES Catalog {!viewOnly ? 'yet' : ''}
                </TableCell>
              </TableRow>
            ) : (
              catalogQuotes.map(vendorQuote => (
                <TableRow key={vendorQuote.VendorQuoteId}>
                  <TableCell>{vendorQuote.VendorName ?? '-'}</TableCell>
                  <TableCell>{countLineItemsForVendorQuote(lineItems, vendorQuote.VendorQuoteId)}</TableCell>
                  <TableCell>
                    {!viewOnly && (
                      <IconButton size="small" onClick={() => setDeleteTarget(vendorQuote)}>
                        <DeleteOutlined />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      {!viewOnly && (
        <>
          <AiSupplierQuoteImportDialog open={aiImportOpen} onClose={() => setAiImportOpen(false)} quoteId={quoteId} />
          <ImportSupplierQuoteTemplateDialog
            open={importTemplateOpen}
            onClose={() => setImportTemplateOpen(false)}
            quoteId={quoteId}
          />
          <SupplierQuoteNumberDialog
            open={apiDialogOpen}
            onClose={() => setApiDialogOpen(false)}
            quoteId={quoteId}
            supplierName={activeApiSupplier ?? ''}
            endpoint={activeConfig?.endpoint ?? ''}
            fieldLabel={activeConfig?.fieldLabel ?? ''}
            bodyKey={activeConfig?.bodyKey ?? ''}
          />
          <ConfirmDialog
            open={deleteTarget !== null}
            title="Remove Supplier Quote?"
            body="Are you sure you want to remove the supplier quote from the main quote?"
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteTarget(null)}
            confirmLabel="Remove Supplier Quote"
            cancelLabel="Cancel"
          />
        </>
      )}
    </div>
  )
}
